import gql from 'graphql-tag'

/**
 * NOTE: if you add or remove from this, ensure you
 * also add to the {@link GET_SURVEY_REVIEW} and/or {@link GET_USER_SALARIES}
 * and/or {@link SEARCH_SALARIES_FOR_HOME} query
 */
export const SEARCH_SALARIES = gql`
  query searchSalary($input: SalarySearchInput!) {
    searchSalary(input: $input) {
      data {
        academicAffiliation
        anyBonuses
        anyOtherIncome
        tailCoverage
        ancillaryIncomeSource
        annualEstPartnerIncome
        annualEstRankAdjustment
        annualLeadershipStipend
        annualNonClinicalIncome
        annualEstAncillaryIncome
        avgHoursWorked
        baseSalary
        basePayPerHour
        bonus
        callPaid
        callPaidRate
        callSchedule
        callScheduleOther
        callScheduleDetails
        callType
        displayLocation
        clinicalPct
        cmeAllowance
        cmeTimeWeeks
        collectionsPct
        compLoanAssistance
        compModel
        compOtherDetails
        compSatisfaction
        compTargetWrvu
        postedAt
        dayOffPostCall
        employer401aMatch
        employer401kMatch
        employer403bMatch
        employer457bMatch
        retirementPlans
        employerCoversMalpractice
        employerType
        employerTypeDetails
        estimatedFte
        insuranceHealthWellnessBenefits
        taxStatus
        jobType
        hourlyRateDifferential
        leadershipPct
        loanAssistance
        newJob
        nightlyHourlyRate
        occupation {
          baseTitlePlusSpecialty
          profession {
            name
          }
        }
        residencyStipend
        overtimeIncome
        overtimeRatePerHour
        annualEstPartnerIncome
        annualEstRankAdjustment
        annualNonClinicalIncome
        annualEstAncillaryIncome
        otherBonus
        otherIncome
        otherNotableBenefits
        otherNoteworthyComp
        paidTimeOffWeeks
        parentalLeaveWeeks
        pensionContribution
        productivityBonus
        qualityBonus
        relocationBonus
        researchPct
        residencyStipend
        responsibilities
        salaryId
        scheduledDays
        scheduledHours
        shiftTiming
        signingBonus
        submittedByCurrentUser
        tailCoverage
        teachingPct
        userLikes
        visibility
        visibilityReason
        weekendHourlyRate
        wrvuRate
        yearsOfExperience
        compTotal {
          base
          bonus
          other
          total
        }
        locums {
          isCallStipend
          agencyBenefits
          agencyFeedback
          agencyName
          agencyRating
          callRatePerHour
          callStipendWeekendRate
          callStipendWeeklyRate
          contractActiveThruDate
          contractingEntity
          employerName
          guaranteedHrsPerDay
        }
        practiceSetting
        practiceSettingOther
      }
      meta {
        totalCount
        pagesCount
        pageSize
        pageNumber
      }
    }
  }
`

/**
 * NOTE: if you add or remove from this, ensure you
 * also add to the {@link GET_SURVEY_REVIEW} & {@link SEARCH_SALARIES} & {@link GET_USER_SALARIES} query
 */
export const SEARCH_SALARIES_FOR_HOME = gql`
  query searchSalary($input: SalarySearchInput!) {
    searchSalary(input: $input) {
      data {
        academicAffiliation
        anyBonuses
        anyOtherIncome
        avgHoursWorked
        baseSalary
        bonus
        callSchedule
        displayLocation
        compModel
        employerType
        employerTypeDetails
        taxStatus
        jobType
        postedAt
        hourlyRateDifferential
        leadershipPct
        insuranceHealthWellnessBenefits
        occupation {
          slug
          baseTitlePlusSpecialty
          specialtyId
          specialty {
            baseSpecialtyId
          }
          profession {
            slug
          }
        }
        otherBonus
        otherIncome
        paidTimeOffWeeks
        salaryId
        yearsOfExperience
        visibility
      }
    }
  }
`

export const LIKE_SALARY = gql`
  mutation likeSalary($input: SalaryLikedInput!) {
    likeSalary(input: $input) {
      salaryId
      userLikes
    }
  }
`

export const FLAG_SALARY = gql`
  mutation flagSalary($input: SalaryFlaggedInput!) {
    flagSalary(input: $input)
  }
`

export const GET_FEATURED_EMPLOYERS = gql`
  query searchEmployer($input: EmployerSearchInput!) {
    searchEmployer(input: $input) {
      results {
        id
        name
        logoUrl
        websiteUrl
        employerTypeDetails
        verified
        description
        jobsUrl
      }
    }
  }
`

export const GET_SALARY_HISTOGRAM_DATA = gql`
  query getHistogram($input: SalaryHistogramInput!) {
    getHistogram(input: $input) {
      avgTotalComp
      baseSalary
      bonuses
      bonusesPct
      otherIncome
      otherIncomePct
      workloadHoursPerWeek
      paidTimeOffWeeks
      salaryCount
      histogram
      histogramTitle
      updatedAt
      hasTrends
    }
  }
`

export const GET_SALARY_HISTOGRAM_DATA_FOR_SEO = gql`
  query getHistogram($input: SalaryHistogramInput!) {
    getHistogram(input: $input) {
      avgTotalComp
      baseSalary
      bonuses
      otherIncome
      histogramTitle
      updatedAt
      salaryCount
    }
  }
`

export const GET_OCCUPATION = gql`
  query getOccupation($input: OccupationGetInput!) {
    getOccupation(input: $input) {
      profession {
        name
        id
        slug
      }
      specialty {
        name
        id
        slug
      }
      slug
    }
  }
`

export const GET_ALL_OCCUPATIONS = gql`
  query getAllOccupations {
    getAllOccupations {
      profession {
        slug
      }
      specialty {
        slug
      }
      slug
    }
  }
`

export const GET_SEO_LINKS_FOR_PAGE = gql`
  query getSeolinksForPage($input: SeoPageLinksInput!) {
    getSeolinksForPage(input: $input) {
      seoLinkGroups {
        groupType
        groupName
        seoLinks {
          occupation {
            slug
            baseTitlePlusSpecialty
            fullTitle
            specialty {
              slug
              fullName
            }
            profession {
              slug
              name
            }
          }
          profession {
            slug
            name
          }
          jobType
          state {
            slug
            name
          }
        }
      }
    }
  }
`

export const SALARY_TREND_FIELDS = gql`
  fragment SalaryTrendTypeFields on SalaryTrendType {
    groupType
    groupLabel
    occupation {
      baseTitlePlusSpecialty
      slug
      fullTitle
      specialty {
        fullName
      }
    }
    salaryCount
    payPeriod
    avgTotal
    avgSalary
    avgBonuses
    avgOtherIncome
    avgSigningBonus
    avgRelocationBonus
    avgFte
    avgHoursPerWeek
    avgCmeAllowance
    avgCmeTimeOffWeeks
    avgParentalLeaveWeeks
    avgPaidTimeOffWeeks
    avgSatisfactionRating
  }
`

export const GET_SALARY_TRENDS = gql`
  query GetSalaryTrends($input: SalaryTrendsInput!) {
    getSalaryTrends(input: $input) {
      specialtyTrends {
        ...SalaryTrendTypeFields
      }
      yoeTrends {
        ...SalaryTrendTypeFields
      }
      genderTrends {
        ...SalaryTrendTypeFields
      }
      employerTypeTrends {
        ...SalaryTrendTypeFields
      }
      academicAffiliationTrends {
        ...SalaryTrendTypeFields
      }
      taxStatusTrends {
        ...SalaryTrendTypeFields
      }
      compModelTrends {
        ...SalaryTrendTypeFields
      }
      countryRegionTrends {
        ...SalaryTrendTypeFields
      }
      metroTypeTrends {
        ...SalaryTrendTypeFields
      }
      colTrends {
        ...SalaryTrendTypeFields
      }
    }
  }
  ${SALARY_TREND_FIELDS}
`
